import {
	Box,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { Fragment } from "react";
import { Controller } from "react-hook-form";

import { TensileRequirementByGrade } from "@/pages/configuration/models/TensileRequirementByGrade";
import { backgroundColor } from "@/utils/theme";
import { getMomentTz } from "@dexteel/mesf-core";
import { HeatMechanicalResult } from "../../models/HeatMechanicalResult";
import { MechanicalTestRunDisposition } from "../../models/MechanicalTestRunDisposition";
import {
	ChangedValue,
	DispositionSelect,
	ResultChip,
	RunHistoryBar,
	SeqLabel,
	StatusChip,
} from "./cells";
import { CustomInput } from "./CustomInput";
import { disposedByLabel, formatTimestamp } from "./format";
import {
	VALUE_COLUMNS,
	changedCellSx,
	headerCellSx,
	refCellSx,
	runRefCellSx,
} from "./layout";
import { RunActions } from "./RunActions";
import {
	RunGroup,
	buildRunGroups,
	overallChangeKey,
	valueChangeKey,
} from "./runGroups";
import { MechanicalRunEdit } from "./types";
import { useRunEditor } from "./useRunEditor";
import { RunHistoryView, useRunHistory } from "./useRunHistory";

const moment = getMomentTz();

export type {
	Disposition,
	MechanicalResultValues,
	MechanicalRunEdit,
} from "./types";

type Props = {
	results: HeatMechanicalResult[];
	requirement?: TensileRequirementByGrade | null;
	dispositions?: MechanicalTestRunDisposition[];
	onSaveRun?: (
		mechanicalTestRunId: number,
		edit: MechanicalRunEdit,
	) => Promise<unknown> | void;
	isSaving?: boolean;
	maxHeight?: number | string;
	allowEmptyRun?: boolean;
	showSeqColors?: boolean;
};

export const MechanicalResultsTable = ({
	results,
	requirement = null,
	dispositions = [],
	onSaveRun,
	isSaving = false,
	maxHeight = 420,
	allowEmptyRun = false,
	showSeqColors = false,
}: Props) => {
	const groups = buildRunGroups(results, dispositions, allowEmptyRun);
	const editor = useRunEditor({ onSaveRun });
	const history = useRunHistory();
	const canEdit = !!onSaveRun;

	const hasMixedLimits =
		new Set(
			groups
				.filter((group) => group.results.length > 0)
				.map((group) => group.results[0].RequirementId),
		).size > 1;

	const hasRunRef = (shown: RunGroup) =>
		hasMixedLimits && shown.results.length > 0;

	const revTooltip = (
		rev: number | null | undefined,
		effectiveDate: Date | string | null | undefined,
	) =>
		`Rev ${rev}, effective ${effectiveDate ? moment(effectiveDate).format("MM/DD/YY") : "-"}`;

	const refTooltip = requirement
		? revTooltip(requirement.Rev, requirement.EffectiveDate)
		: "";

	const renderRunCells = (
		group: RunGroup,
		shown: RunGroup,
		view: RunHistoryView | null,
	) => {
		const isEditing = editor.isEditing(group.runId);
		const rowSpan =
			Math.max(shown.results.length, 1) + (hasRunRef(shown) ? 1 : 0);
		const dispositionFrom = view?.updatedFields.get("disposition");

		return (
			<>
				<TableCell
					rowSpan={rowSpan}
					sx={{ borderLeft: 1, borderColor: "divider" }}
					align="center"
				>
					{formatTimestamp(shown.reported)}
				</TableCell>
				<TableCell
					rowSpan={rowSpan}
					align="center"
					sx={{
						borderLeft: 1,
						borderColor: "divider",
						...(dispositionFrom !== undefined
							? changedCellSx("disposition", shown.disposition?.Disposition)
							: {}),
					}}
				>
					{isEditing ? (
						<Controller
							name="disposition"
							control={editor.control}
							render={({ field }) => (
								<DispositionSelect
									value={field.value}
									disabled={isSaving}
									onChange={field.onChange}
								/>
							)}
						/>
					) : (
						<ChangedValue previous={dispositionFrom}>
							<StatusChip code={shown.disposition?.Disposition} />
						</ChangedValue>
					)}
				</TableCell>
				<TableCell rowSpan={rowSpan} align="center">
					{disposedByLabel(shown.disposition)}
				</TableCell>
				<TableCell rowSpan={rowSpan} align="center">
					{formatTimestamp(shown.disposition?.DisposedAt)}
				</TableCell>
				<TableCell
					rowSpan={rowSpan}
					sx={{ whiteSpace: "normal", color: "text.secondary" }}
					align="center"
				>
					{isEditing ? (
						<TextField
							name={editor.commentField.name}
							onChange={editor.commentField.onChange}
							onBlur={editor.commentField.onBlur}
							inputRef={editor.commentField.ref}
							placeholder="Comment"
							variant="outlined"
							size="small"
							multiline
							rows={2}
							disabled={isSaving}
							autoComplete="off"
							sx={{
								width: "100%",
								backgroundColor: (theme) => theme.palette.background.paper,
								"& .MuiOutlinedInput-root": { p: "5px 8px" },
							}}
							inputProps={{ style: { fontSize: 11, padding: 0 } }}
						/>
					) : (
						<Tooltip arrow title={shown.comment ?? ""}>
							<Box
								sx={{
									display: "-webkit-box",
									WebkitBoxOrient: "vertical" as const,
									WebkitLineClamp: 2,
									overflow: "hidden",
								}}
							>
								{shown.comment ?? "-"}
							</Box>
						</Tooltip>
					)}
				</TableCell>
				<TableCell rowSpan={rowSpan} align="right">
					<RunActions
						group={group}
						editor={editor}
						history={history}
						canEdit={canEdit}
						isSaving={isSaving}
					/>
				</TableCell>
			</>
		);
	};

	return (
		<TableContainer sx={{ width: "100%", overflowX: "auto", maxHeight }}>
			<Table
				stickyHeader
				size="small"
				sx={{
					minWidth: 1000,
					tableLayout: "fixed",
					"& .MuiTableCell-root": { fontSize: 11, px: 1 },
				}}
			>
				<TableHead>
					<TableRow>
						<TableCell sx={{ ...headerCellSx, minWidth: 80 }} width="8%">
							Seq
						</TableCell>
						{VALUE_COLUMNS.map((column) => (
							<TableCell
								key={column.field}
								sx={{ ...headerCellSx, minWidth: 78 }}
								width="8%"
							>
								{column.label}
							</TableCell>
						))}
						<TableCell
							sx={{ ...headerCellSx, minWidth: 60 }}
							width="6%"
							align="center"
						>
							Test
						</TableCell>
						<TableCell sx={headerCellSx} width="9%" align="center">
							Reported At
						</TableCell>
						<TableCell
							sx={{ ...headerCellSx, borderLeft: 1, borderColor: "divider" }}
							width="9%"
							align="center"
						>
							Disposition
						</TableCell>
						<TableCell sx={headerCellSx} width="6%" align="center">
							By
						</TableCell>
						<TableCell sx={headerCellSx} width="9%" align="center">
							At
						</TableCell>
						<TableCell sx={headerCellSx} width="13%" align="center">
							Comment
						</TableCell>
						<TableCell sx={headerCellSx} width="8%" />
					</TableRow>
				</TableHead>

				<TableBody>
					{groups.map((group: RunGroup, groupIndex: number) => {
						const view = history.viewOf(group);
						const shown = view?.group ?? group;
						const isEditing = editor.isEditing(group.runId);
						const isPast = !!view && !view.isCurrent && !!view.at;

						const rowSx = (isFirstRowOfGroup: boolean) => ({
							...(groupIndex > 0 && isFirstRowOfGroup && !view
								? { borderTop: 2, borderColor: "divider" }
								: {}),
							...(isEditing ? { backgroundColor } : {}),
							...(isPast ? { bgcolor: "action.hover" } : {}),
						});

						return (
							<Fragment key={group.runId}>
								{view && (
									<TableRow
										sx={
											groupIndex > 0
												? { borderTop: 2, borderColor: "divider" }
												: undefined
										}
									>
										<TableCell
											colSpan={VALUE_COLUMNS.length + 8}
											sx={{ bgcolor: "action.hover", py: 0 }}
										>
											<RunHistoryBar view={view} />
										</TableCell>
									</TableRow>
								)}

								{shown.results.length === 0 ? (
									<TableRow sx={rowSx(true)}>
										<TableCell
											colSpan={VALUE_COLUMNS.length + 2}
											sx={{ color: "text.secondary" }}
										>
											No specimens loaded
										</TableCell>
										{renderRunCells(group, shown, view)}
									</TableRow>
								) : (
									shown.results.map((result, resultIndex) => (
										<TableRow
											key={result.HeatMechanicalResultId}
											sx={rowSx(resultIndex === 0)}
										>
											<TableCell>
												<SeqLabel
													sequence={result.SampleSequence}
													showColor={showSeqColors}
												/>
											</TableCell>
											{VALUE_COLUMNS.map((column) => {
												const updatedFrom = view?.updatedFields.get(
													valueChangeKey(
														result.MechanicalSampleId,
														column.field,
													),
												);

												return (
													<TableCell
														key={column.field}
														sx={{
															color:
																column.passed(result) === false
																	? "error.main"
																	: undefined,
															fontWeight:
																column.passed(result) === false
																	? 600
																	: undefined,
															...(updatedFrom !== undefined
																? changedCellSx("results")
																: {}),
														}}
													>
														{isEditing ? (
															<CustomInput
																{...editor.valueField(
																	resultIndex,
																	column.field,
																)}
																disabled={isSaving}
															/>
														) : (
															<ChangedValue previous={updatedFrom}>
																{column.format(result)}
															</ChangedValue>
														)}
													</TableCell>
												);
											})}
											<TableCell
												align="center"
												sx={
													view?.updatedFields.has(
														overallChangeKey(result.MechanicalSampleId),
													)
														? changedCellSx("results")
														: undefined
												}
											>
												<ChangedValue
													previous={view?.updatedFields.get(
														overallChangeKey(result.MechanicalSampleId),
													)}
												>
													<ResultChip passed={result.OverallPassed} />
												</ChangedValue>
											</TableCell>
											{resultIndex === 0 && renderRunCells(group, shown, view)}
										</TableRow>
									))
								)}

								{hasRunRef(shown) && (
									<TableRow sx={rowSx(false)}>
										<TableCell sx={runRefCellSx}>
											{shown.results[0].RequirementId != null ? (
												<Tooltip
													title={revTooltip(
														shown.results[0].RequirementRev,
														shown.results[0].RequirementEffectiveDate,
													)}
													arrow
												>
													<span>{`Ref · Rev ${shown.results[0].RequirementRev}`}</span>
												</Tooltip>
											) : (
												"Ref · None"
											)}
										</TableCell>
										{VALUE_COLUMNS.map((column) => (
											<TableCell key={column.field} sx={runRefCellSx}>
												{shown.results[0].RequirementId != null
													? column.range(shown.results[0])
													: "-"}
											</TableCell>
										))}
										<TableCell sx={runRefCellSx} />
									</TableRow>
								)}
							</Fragment>
						);
					})}
				</TableBody>

				{!hasMixedLimits && (
					<TableFooter>
						<TableRow>
							<TableCell sx={refCellSx}>
								{refTooltip ? (
									<Tooltip title={refTooltip} arrow>
										<span>
											{`Ref${requirement?.Rev != null ? ` · Rev ${requirement.Rev}` : ""}`}
										</span>
									</Tooltip>
								) : (
									"Ref"
								)}
							</TableCell>
							{VALUE_COLUMNS.map((column) => (
								<TableCell key={column.field} sx={refCellSx}>
									{requirement ? column.range(requirement) : "-"}
								</TableCell>
							))}
							{[0, 1, 2, 3, 4, 5, 6].map((index) => (
								<TableCell key={index} sx={refCellSx} />
							))}
						</TableRow>
					</TableFooter>
				)}
			</Table>
		</TableContainer>
	);
};
